import {
  Button,
  Input,
  useDebounceCallback,
} from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

type FormsFilterProps = {
  categories?: Array<{ id: string; name: string }>;
};

export function FormsFilter({ categories = [] }: FormsFilterProps) {
  const nav = useNavigate({ from: "/app/forms" });
  const search = useSearch({ from: "/app/forms" }) as OffsetPageQueryDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const nameFilter = findCondition(filters, "name");
  const categoryFilter = findCondition(filters, "categoryId");
  const statusFilter = findCondition(filters, "status");

  const [searchValue, setSearchValue] = useState<string>(
    (nameFilter?.value as string) || "",
  );

  useEffect(
    () => setSearchValue((nameFilter?.value as string) || ""),
    [nameFilter?.value],
  );

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = upsertCondition(filters, {
        field: "name",
        op: "contains",
        value: val,
      });
    } else {
      nextFilters = removeConditions(filters, "name");
    }
    nav({
      search: {
        offset: 0,
        limit: search.limit ?? 20,
        sort: search.sort as
          | Array<{ field: string; dir: "asc" | "desc" }>
          | undefined,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const categoryValue = (categoryFilter?.value as string) || "all";
  const statusValue = (statusFilter?.value as string) || "all";

  const hasFilter =
    !!nameFilter?.value ||
    !!categoryFilter?.value ||
    !!statusFilter?.value;

  return (
    <div className="mb-4 flex flex-col gap-2 px-2" data-tourid="forms-filter">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຫາຟອມຕາມຊື່, ID ຫຼືເຈົ້າຂອງ..."
            defaultValue={searchValue}
            onChange={(e) => debounced(e.target.value)}
            className="h-8 sm:max-w-2xs"
          />
          <SimpleSelect
            value={categoryValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "categoryId");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "categoryId",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  offset: 0,
                  limit: search.limit ?? 20,
                  sort: search.sort as
                    | Array<{ field: string; dir: "asc" | "desc" }>
                    | undefined,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ໝວດໝູ່: ທັງໝົດ"
            className="h-8 w-full sm:w-40"
            options={[
              { value: "all", label: "ທັງໝົດ" },
              ...categories
                .filter((cat) => cat?.id && cat?.name)
                .map((cat) => ({
                  value: String(cat.id),
                  label: String(cat.name),
                })),
            ]}
          />
          <SimpleSelect
            value={statusValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "status");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "status",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  offset: 0,
                  limit: search.limit ?? 20,
                  sort: search.sort as
                    | Array<{ field: string; dir: "asc" | "desc" }>
                    | undefined,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ສະຖານະ: ທັງໝົດ"
            className="h-8 w-full sm:w-40"
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "active", label: "ໃຊ້ງານ" },
              { value: "under_revision", label: "ກຳລັງປັບປຸງ" },
              { value: "discontinued", label: "ຢຸດໃຊ້ງານ" },
              { value: "draft", label: "ຮ່າງ" },
            ]}
          />
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const currentSort = search.sort as
                | Array<{ field: string; dir: "asc" | "desc" }>
                | undefined;
              nav({
                search: {
                  offset: 0,
                  limit: search.limit ?? 20,
                  sort: currentSort,
                  filters: undefined,
                },
              });
            }}
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
